import React, { useState } from 'react';
import { Handshake, CalendarCheck } from 'lucide-react';
import { leadsService } from '../../../services/api';
import styles from './GetInTouch.module.css';

const TIME_WINDOWS = ['Morning', 'Afternoon', 'Evening'];

const BLANK = {
  partnership: {
    name: '', organisation: '', email: '', phone: '', region: '', message: '', website: ''
  },
  demo: {
    name: '', organisation: '', email: '', phone: '', role: '',
    preferred_date: '', preferred_window: '', message: '', website: ''
  }
};

const PARTNER_FIELDS = [
  { key: 'name', label: 'Your name', type: 'text', required: true, autoComplete: 'name' },
  { key: 'organisation', label: 'Organisation', type: 'text', required: true, autoComplete: 'organization' },
  { key: 'email', label: 'Work email', type: 'email', required: true, autoComplete: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { key: 'region', label: 'Region or territory', type: 'text', wide: true, placeholder: 'Where you would operate' },
  { key: 'message', label: 'About your organisation', type: 'textarea', wide: true, placeholder: 'Markets you serve, and what you want from a partnership' }
];

const DEMO_FIELDS = [
  { key: 'name', label: 'Your name', type: 'text', required: true, autoComplete: 'name' },
  { key: 'organisation', label: 'Organisation', type: 'text', required: true, autoComplete: 'organization' },
  { key: 'email', label: 'Work email', type: 'email', required: true, autoComplete: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { key: 'role', label: 'Your role', type: 'text', placeholder: 'Principal, IT lead, administrator' },
  { key: 'preferred_date', label: 'Preferred date', type: 'date' },
  { key: 'preferred_window', label: 'Preferred time', type: 'select' },
  { key: 'message', label: 'Anything to focus on', type: 'textarea', wide: true, placeholder: 'Reports or modules you most want to see' }
];

const TABS = [
  {
    key: 'partnership',
    label: 'Channel Partnership',
    Icon: Handshake,
    blurb: 'Resell and implement Analytica X in your region.',
    submitLabel: 'Send enquiry',
    fields: PARTNER_FIELDS
  },
  {
    key: 'demo',
    label: 'Schedule a Demo',
    Icon: CalendarCheck,
    blurb: 'Tell us when suits and we will confirm a time.',
    submitLabel: 'Request demo',
    fields: DEMO_FIELDS
  }
];

const LeadForm = ({ tab }) => {
  const [values, setValues] = useState(BLANK[tab.key]);
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');

  const set = (key) => (event) =>
    setValues((prev) => ({ ...prev, [key]: event.target.value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setState('sending');
    try {
      await leadsService.submit({
        ...values,
        lead_type: tab.key,
        source_page: typeof window !== 'undefined' ? window.location.pathname : ''
      });
      setState('sent');
    } catch (err) {
      setError(err.message || 'Could not send your enquiry. Please try again.');
      setState('idle');
    }
  };

  if (state === 'sent') {
    return (
      <p className={styles.sent}>
        Thank you. We have your details and will be in touch shortly.
      </p>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      {tab.fields.map((field) => (
        <label
          key={field.key}
          className={field.wide ? `${styles.field} ${styles.fieldWide}` : styles.field}
          htmlFor={`${tab.key}-${field.key}`}
        >
          <span className={styles.label}>
            {field.label}
            {field.required ? <span aria-hidden="true"> *</span> : null}
          </span>

          {field.type === 'textarea' ? (
            <textarea
              id={`${tab.key}-${field.key}`}
              rows={3}
              value={values[field.key]}
              onChange={set(field.key)}
              required={field.required}
              placeholder={field.placeholder}
            />
          ) : field.type === 'select' ? (
            <select
              id={`${tab.key}-${field.key}`}
              value={values[field.key]}
              onChange={set(field.key)}
            >
              <option value="">No preference</option>
              {TIME_WINDOWS.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          ) : (
            <input
              id={`${tab.key}-${field.key}`}
              type={field.type}
              value={values[field.key]}
              onChange={set(field.key)}
              required={field.required}
              placeholder={field.placeholder}
              autoComplete={field.autoComplete}
            />
          )}
        </label>
      ))}

      {/* Hidden from people, so anything typed here came from a bot */}
      <input
        className={styles.honey}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={values.website}
        onChange={set('website')}
        aria-hidden="true"
      />

      {error ? <p className={styles.error}>{error}</p> : null}

      <button type="submit" className={styles.submit} disabled={state === 'sending'}>
        {state === 'sending' ? 'Sending…' : tab.submitLabel}
      </button>

      <p className={styles.consent}>
        We will use these details only to respond to your enquiry.
      </p>
    </form>
  );
};

const GetInTouch = () => {
  const [active, setActive] = useState('partnership');
  const tab = TABS.find((t) => t.key === active) || TABS[0];

  return (
    <section id="get-in-touch" className={`${styles.section} section`}>
      <div className="container">
        <h2 className="section-title fade-in">Work With Us</h2>
        <p className={styles.lead}>
          Partner with us to bring Analytica X to your market, or see the platform
          running against a dataset like your own.
        </p>

        <div className={styles.split}>
          <figure className={styles.visual}>
            <img
              src="/images/partnership.jpg"
              alt="A group of colleagues joining hands over a table"
              loading="lazy"
            />
          </figure>

          <div className={styles.panel}>
            <div className={styles.tabBar} role="tablist" aria-label="Enquiry type">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  id={`tab-${t.key}`}
                  aria-selected={active === t.key}
                  aria-controls={`panel-${t.key}`}
                  className={active === t.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                  onClick={() => setActive(t.key)}
                >
                  <t.Icon size={16} strokeWidth={2} aria-hidden="true" />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>

            <div
              className={styles.panelBody}
              role="tabpanel"
              id={`panel-${tab.key}`}
              aria-labelledby={`tab-${tab.key}`}
            >
              <p className={styles.blurb}>{tab.blurb}</p>
              {/* Keyed so switching tabs gives a fresh form rather than
                  carrying the other enquiry's values across */}
              <LeadForm key={tab.key} tab={tab} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GetInTouch;
